/**
 * Funzioni pure della pagina «Eval dell'agente»: formattazione delle quote, raggruppamenti,
 * lettura degli errori. Senza React, per essere testate da sole.
 */

/**
 * `8/10 (80%, IC 95% 49–94%)`: la stessa forma del terminale (`yarn eval:agent`).
 * @param {number} successi - Esecuzioni verdi
 * @param {number} prove - Esecuzioni che contano
 * @param {{basso: number, alto: number}} [intervallo] - Intervallo di Wilson dal server
 * @returns {string} La quota leggibile
 */
export const descriviQuota = (successi, prove, intervallo) => {
  if (!prove) return '—'
  const percento = (v) => Math.round(v * 100)
  const base = `${successi}/${prove} (${percento(successi / prove)}%`
  return intervallo
    ? `${base}, IC 95% ${percento(intervallo.basso)}–${percento(intervallo.alto)}%)`
    : `${base})`
}

/**
 * Gli scenari raggruppati per il primo tag, nell'ordine del catalogo.
 * @param {Array<{id: string, tag: string[]}>} scenari - Il catalogo
 * @returns {Array<{tag: string, scenari: Array<Object>}>} I gruppi
 */
export const perTag = (scenari = []) => {
  const gruppi = new Map()
  for (const scenario of scenari) {
    const tag = scenario.tag?.[0] ?? 'altri'
    if (!gruppi.has(tag)) gruppi.set(tag, [])
    gruppi.get(tag).push(scenario)
  }
  return [...gruppi.entries()].map(([tag, lista]) => ({ tag, scenari: lista }))
}

/**
 * Un controllo dello scenario, in una riga. Il testo è quello del JSON: i nomi dei campi
 * restano come sono scritti.
 * @param {Object} controllo - Un elemento di `controlli`
 * @returns {string} La riga
 */
export const descriviControllo = (controllo) => {
  if (!controllo?.tipo) return ''
  const turno =
    Number.isInteger(controllo.turno) && controllo.turno !== -1 ? ` [turno ${controllo.turno}]` : ''
  switch (controllo.tipo) {
    case 'toolUsato':
    case 'toolNonUsato':
      return `${controllo.tipo}: ${controllo.tool}${turno}`
    case 'stopReason':
      return `stopReason: ${controllo.valore}${turno}`
    case 'numeroServizi':
      return `numeroServizi: ${controllo.sezione} = ${controllo.numero}${turno}`
    case 'servizio':
      return `servizio: ${controllo.quantificatore} ${controllo.sezione}.${controllo.campo} /${controllo.regex}/${controllo.descrizione ? ` — ${controllo.descrizione}` : ''}${turno}`
    case 'campoRichiesta':
      return controllo.regex
        ? `campoRichiesta: ${controllo.percorso} /${controllo.regex}/${turno}`
        : `campoRichiesta: ${controllo.percorso} ${controllo.presente === false ? 'assente' : 'presente'}${turno}`
    case 'statoCapability':
      return `statoCapability: ${controllo.capability}.${controllo.campo} /${controllo.regex}/${turno}`
    case 'testoContiene':
    case 'testoNonContiene':
      return `${controllo.tipo}: /${controllo.regex}/${turno}`
    case 'giudizio':
      return `giudizio «${controllo.nome}»: ${controllo.criterio}${turno}`
    default:
      return `${controllo.tipo}${turno}`
  }
}

/**
 * Una mossa del copione: i tool imposti, con gli argomenti in una riga.
 * @param {{testo?: string, toolCalls?: Array<{name: string, args?: Object}>}} mossa
 * @returns {string} La riga
 */
export const descriviMossa = (mossa) => {
  const chiamate = (mossa?.toolCalls ?? [])
    .map((chiamata) => {
      const args = chiamata.args ? ` ${JSON.stringify(chiamata.args)}` : ''
      return `${chiamata.name}${args}`
    })
    .join('; ')
  return [mossa?.testo, chiamate].filter(Boolean).join(' — ')
}

/**
 * Il riassunto di una run per l'elenco: scenari sopra soglia su quelli eseguiti.
 * @param {{risultati?: Array<{saltato?: string, passato: boolean}>}} run - La run
 * @returns {{verdi: number, eseguiti: number, saltati: number}} I conteggi
 */
/** Il verdetto esplicito prevale su `proveAttese`, rimasto per errore in alcune run storiche. */
export const scenarioChiuso = (risultato) =>
  !risultato?.saltato && typeof risultato?.passato === 'boolean'

export const riassuntoRun = (run) => {
  if (!run?.risultati && run?.scenari)
    return {
      verdi: run.scenari.sopraSoglia ?? 0,
      eseguiti: run.scenari.chiusi ?? 0,
      saltati: run.scenari.saltati ?? 0
    }
  const risultati = run?.risultati ?? []
  const chiusi = risultati.filter(scenarioChiuso)
  return {
    verdi: chiusi.filter((r) => r.passato).length,
    eseguiti: chiusi.length,
    saltati: risultati.filter((r) => r.saltato).length
  }
}

/**
 * I passi di una run: fatti, totali, verdi, errori. Una run vecchia non li ha, e si
 * ricostruiscono dai risultati già chiusi.
 * @param {Object} run - La run
 * @returns {{fatti: number, totali: number, verdi: number, errori: number}} I passi
 */
export const passiRun = (run) => {
  if (run?.passi) return run.passi
  const eseguite = (run?.risultati ?? []).filter((r) => !r.saltato)
  const successi = eseguite.reduce((somma, r) => somma + (r.successi ?? 0), 0)
  const prove = eseguite.reduce((somma, r) => somma + (r.prove ?? 0), 0)
  return {
    fatti: run?.progresso?.fatte ?? prove,
    totali: run?.progresso?.totali ?? prove,
    verdi: successi,
    // Le run storiche non distinguevano errori di esecuzione, rossi e saltati.
    rossi: null,
    errori: null,
    nonValidi: null,
    saltati: null
  }
}

/**
 * Il nome da mostrare: quello della run, poi l'etichetta, poi la data.
 * @param {{nome?: string, etichetta?: string, avviataIl?: string}} run - La run
 * @returns {string} Il nome
 */
const testoScenario = (scenario) =>
  String(scenario?.situazione || scenario?.descrizione || scenario?.id || '')
    .replace(/\s+/g, ' ')
    .trim()

/**
 * Il nome di una run, anche di una salvata prima che il nome esistesse.
 * @param {{nome?: string, etichetta?: string, risultati?: Array<Object>}} run - La run
 * @returns {string} Il nome
 */
export const nomeRun = (run) => {
  if (run?.nome) return run.nome
  if (run?.etichetta) return run.etichetta
  const eseguiti = (run?.risultati ?? []).filter((r) => !r.saltato)
  if (!eseguiti.length) return ''
  if (eseguiti.length === 1) return testoScenario(eseguiti[0]).slice(0, 80)
  if (eseguiti.length <= 3) return eseguiti.map(testoScenario).join(' · ').slice(0, 80)
  const aree = [...new Set(eseguiti.map((r) => r.tag?.[0]).filter(Boolean))]
  return `${aree.length === 1 ? aree[0] : 'misto'} · ${eseguiti.length} scenari`
}

/**
 * Il costo di una run, sommato per valuta dalle esecuzioni già chiuse.
 * @param {{risultati?: Array<{costo?: Array<{valuta: string, totale: number, tokenIn?: number, tokenOut?: number}>}>}} run
 * @returns {Array<{valuta: string, totale: number, tokenIn: number, tokenOut: number}>} Le voci
 */
export const costoRun = (run) => {
  const daiRisultati = sommaCosti((run?.risultati ?? []).flatMap((r) => r.costo ?? []))
  // In corso il costo sta sulla run: i risultati di uno scenario arrivano solo quando chiude.
  if (run?.stato === 'in_corso' && run.costo?.length) return sommaCosti(run.costo)
  return daiRisultati.length ? daiRisultati : sommaCosti(run?.costo ?? [])
}

/**
 * Somma voci di costo della stessa valuta. Valute diverse restano separate.
 * @param {Array<{valuta: string, totale: number, tokenIn?: number, tokenOut?: number}>} voci
 * @returns {Array<{valuta: string, totale: number, tokenIn: number, tokenOut: number}>}
 */
export const sommaCosti = (voci = []) => {
  const perValuta = new Map()
  for (const voce of voci) {
    if (!voce?.valuta || typeof voce.totale !== 'number') continue
    const chiave = `${voce.valuta}|${voce.voce ?? ''}`
    const gia = perValuta.get(chiave) ?? {
      valuta: voce.valuta,
      totale: 0,
      tokenIn: 0,
      tokenOut: 0,
      ...(voce.voce ? { voce: voce.voce } : {})
    }
    perValuta.set(chiave, {
      valuta: voce.valuta,
      // Sei decimali come l'archivio: la somma dei float lasciava 0.30000000000000004.
      totale: Math.round((gia.totale + voce.totale) * 1e6) / 1e6,
      tokenIn: gia.tokenIn + (voce.tokenIn ?? 0),
      tokenOut: gia.tokenOut + (voce.tokenOut ?? 0),
      ...(voce.voce ? { voce: voce.voce } : {})
    })
  }
  return [...perValuta.values()]
}

/**
 * Una cifra di costo, leggibile: `0,42 €`. Sotto il centesimo si vedono i millesimi.
 * @param {number} totale - Il costo
 * @param {string} valuta - `EUR`, `USD`…
 * @returns {string} La cifra
 */
export const formattaCosto = (totale, valuta = 'EUR') => {
  if (typeof totale !== 'number' || Number.isNaN(totale)) return '—'
  const simbolo = { EUR: '€', USD: '$' }[valuta] ?? valuta
  const cifre = totale > 0 && totale < 0.01 ? 4 : 2
  return `${totale.toFixed(cifre).replace('.', ',')} ${simbolo}`
}

/**
/**
 * Il nome leggibile di una voce di costo. Il modello resta il suo id; i provider hanno
 * l'etichetta della card dei turni.
 * @param {string} [voce]
 * @returns {string}
 */
export const nomeVoceCosto = (voce) =>
  ({
    google_geocoding: 'Google Geocoding',
    google_directions: 'Google Directions',
    google_routes: 'Google Routes',
    tavily_search: 'Tavily Search',
    tavily_extract: 'Tavily Extract'
  })[voce] ??
  voce ??
  ''

/**
 * Le voci di costo di una run in una riga. Vuoto se la run non ha ancora un costo.
 * @param {Array<{valuta: string, totale: number, voce?: string}>} voci
 * @returns {string} Es. `1,20 € · gpt-6-luna`
 */
/**
 * Il totale per valuta, senza i nomi. È la cifra della card e del dettaglio.
 * @param {Array<{valuta: string, totale: number}>} voci
 * @returns {Array<{valuta: string, totale: number}>}
 */
export const totaliPerValuta = (voci = []) => {
  const perValuta = new Map()
  for (const voce of voci) {
    if (!voce?.valuta || typeof voce.totale !== 'number') continue
    perValuta.set(voce.valuta, (perValuta.get(voce.valuta) ?? 0) + voce.totale)
  }
  return [...perValuta.entries()].map(([valuta, totale]) => ({
    valuta,
    totale: Math.round(totale * 1e6) / 1e6
  }))
}

export const descriviCosto = (voci) =>
  voci?.length
    ? voci
        .map((v) => {
          const cifra = formattaCosto(v.totale, v.valuta)
          const nome = nomeVoceCosto(v.voce)
          return nome ? `${cifra} · ${nome}` : cifra
        })
        .join(' + ')
    : ''

const INTERRUZIONE = '(esecuzione interrotta)'

/**
 * Gli scenari che fanno più fatica, sulle run già concluse.
 *
 * Per ogni scenario si sommano successi e prove (i saltati e gli scenari ancora aperti non
 * contano) e il controllo che cade di più. In cima chi è sotto soglia in più run, a parità
 * chi ha più prove: un rosso su una prova sola dice poco.
 * @param {Array<{stato?: string, risultati?: Array<Object>}>} runs - L'elenco
 * @param {number} [limite=6] - Quanti mostrarne
 */
export const scenariInDifficolta = (runs = [], limite = 6) => {
  const perId = new Map()
  for (const run of runs) {
    if (run.stato === 'in_corso' || run.stato === 'in_coda' || run.stato === 'fallita') continue
    for (const risultato of run.risultati ?? []) {
      if (risultato.saltato || !risultato.prove || typeof risultato.passato !== 'boolean') continue
      const voce = perId.get(risultato.id) ?? {
        id: risultato.id,
        descrizione: risultato.descrizione,
        situazione: risultato.situazione,
        successi: 0,
        prove: 0,
        run: 0,
        runSottoSoglia: 0,
        interruzioni: 0,
        caduti: new Map()
      }
      voce.successi += risultato.successi ?? 0
      voce.prove += risultato.prove
      voce.run += 1
      if (risultato.passato === false) voce.runSottoSoglia += 1
      voce.descrizione = risultato.descrizione || voce.descrizione
      voce.situazione = risultato.situazione || voce.situazione
      for (const caduto of risultato.controlliCaduti ?? []) {
        if (caduto.nome === INTERRUZIONE) {
          voce.interruzioni += caduto.volte ?? 0
          continue
        }
        const gia = voce.caduti.get(caduto.nome) ?? { nome: caduto.nome, volte: 0 }
        gia.volte += caduto.volte ?? 0
        voce.caduti.set(caduto.nome, gia)
      }
      perId.set(risultato.id, voce)
    }
  }
  return [...perId.values()]
    .map((voce) => {
      const caduto = [...voce.caduti.values()].sort((a, b) => b.volte - a.volte)[0]
      return {
        id: voce.id,
        descrizione: voce.descrizione,
        situazione: voce.situazione,
        successi: voce.successi,
        prove: voce.prove,
        run: voce.run,
        runSottoSoglia: voce.runSottoSoglia,
        interruzioni: voce.interruzioni,
        quota: voce.prove ? voce.successi / voce.prove : 0,
        quotaRunSottoSoglia: voce.run ? voce.runSottoSoglia / voce.run : 0,
        quotaFallite: voce.prove ? (voce.prove - voce.successi) / voce.prove : 0,
        quotaInterruzioni: voce.prove ? voce.interruzioni / voce.prove : 0,
        cadutoPiuSpesso: caduto?.nome,
        volteCaduto: caduto?.volte ?? 0,
        quotaCaduto: voce.prove && caduto ? caduto.volte / voce.prove : 0
      }
    })
    .filter((voce) => voce.quota < 1)
    .sort((a, b) => b.quotaRunSottoSoglia - a.quotaRunSottoSoglia || b.prove - a.prove)
    .slice(0, limite)
}

/** Una quota in percentuale intera. Senza denominatore è un trattino. */
export const percento = (numeratore, denominatore) =>
  denominatore ? `${Math.round((numeratore / denominatore) * 100)}%` : '—'

/**
 * Le cifre delle card, sul periodo già filtrato dal server.
 * @param {{run?: number, perStato?: Object, passi?: {fatti: number, verdi: number, errori: number}, costi?: Array<{valuta: string, totale: number}>}} [testata]
 */
export const misureTestata = (testata = {}) => {
  const fatti = testata.passi?.fatti ?? 0
  const verdi = testata.passi?.verdi ?? 0
  const errori = testata.passi?.errori ?? 0
  const nonValidi = testata.passi?.nonValidi ?? 0
  const rossi = testata.passi?.rossi ?? 0
  const saltati = testata.passi?.saltati ?? 0
  const nonClassificati = Math.max(0, fatti - verdi - rossi - errori - nonValidi)
  const run = testata.run ?? 0
  const perStato = testata.perStato ?? {}
  const costi = testata.costi ?? []
  return {
    run,
    perStato,
    fatti,
    verdi,
    errori,
    nonValidi,
    rossi,
    saltati,
    nonClassificati,
    costi,
    costoPerRun: run
      ? totaliPerValuta(costi).map((v) => ({
          ...v,
          totale: Math.round((v.totale / run) * 1e6) / 1e6
        }))
      : [],
    costoPerPasso: fatti
      ? totaliPerValuta(costi).map((v) => ({
          ...v,
          totale: Math.round((v.totale / fatti) * 1e6) / 1e6
        }))
      : []
  }
}

/**
 * Il codice nominato di un errore Feathers arrivato dal proxy, se c'è.
 * @param {unknown} errore - L'errore della query
 * @returns {string|null} Il codice (`RUN_IN_CORSO`…)
 */
export const codiceErrore = (errore) => errore?.data?.code ?? errore?.code ?? null

/**
 * Le opzioni di lancio di una run, anche se l'elenco non porta il documento intero.
 * Quelle salvate vincono. Senza, si ricostruiscono da environment, flusso, scenari e
 * ripetizioni, se sono le stesse per ogni scenario eseguito.
 * @param {Object} run
 * @returns {Object|null}
 */
export const opzioniDi = (run) => {
  if (run?.ripresa?.opzioni?.environmentId) return run.ripresa.opzioni
  const scenari = (run?.risultati ?? []).map((r) => r.id).filter(Boolean)
  const environmentId = Number(run?.environmentId)
  if (!Number.isInteger(environmentId) || environmentId < 1 || !scenari.length) return null
  const numeri = (run.risultati ?? [])
    .filter((r) => !r.saltato)
    .map((r) => r.prove ?? r.esecuzioni?.length)
    .filter((n) => Number.isInteger(n) && n > 0)
  const ripetizioni = numeri.length && numeri.every((n) => n === numeri[0]) ? numeri[0] : undefined
  return {
    environmentId,
    ...(run.contratto?.mode ? { flusso: run.contratto.mode } : {}),
    scenari,
    ...(ripetizioni ? { ripetizioni } : {}),
    ricerca: run.ricerca || 'finta',
    ...(run.google ? { google: run.google } : {})
  }
}

/**
 * Il corpo per rilanciare una run già fatta: stesso environment, flusso, scenari,
 * ripetizioni e ricerca. Il nome resta quello, con un segno che è una ripetizione.
 * @param {Object} run - Il documento della run
 * @returns {Object|null} Il corpo di `create`, o null
 */
export const corpoRipeti = (run) => {
  const opzioni = opzioniDi(run)
  if (!opzioni) return null
  const corpo = corpoRun(
    {
      nome: nomeRun(run),
      environmentId: opzioni.environmentId,
      ricerca: opzioni.ricerca,
      google: opzioni.google,
      flusso: opzioni.flusso,
      scenari: opzioni.scenari ?? [],
      ripetizioni: opzioni.ripetizioni
    },
    []
  )
  return { ...corpo, nome: `${nomeRun(run)} · ripetuta`.slice(0, 80) }
}

/**
 * Le opzioni di una run come le vuole il server: solo i campi valorizzati.
 * @param {Object} form - Lo stato del form
 * @returns {Object} Il corpo di `create`
 */
export const corpoRun = (form, applicabili = []) => {
  const corpo = {
    nome: form.nome?.trim() ?? '',
    environmentId: Number(form.environmentId),
    ricerca: form.ricerca || 'finta'
  }
  // Nessuna scelta vuol dire «tutti quelli che girano qui», non l'intero catalogo: gli altri
  // l'Executor li salterebbe comunque, contandoli come passi fatti.
  const scelti = form.scenari?.length ? form.scenari : applicabili
  if (scelti.length) corpo.scenari = scelti
  if (form.flusso) corpo.flusso = form.flusso
  const ripetizioni = Number(form.ripetizioni)
  if (Number.isInteger(ripetizioni) && ripetizioni > 0) corpo.ripetizioni = ripetizioni
  if (form.google) corpo.google = form.google
  if (form.trascrizioni) corpo.trascrizioni = true
  if (form.campagna) corpo.campagna = form.campagna
  return corpo
}

/**
 * Dove sta uno scenario: comune (con la sua area) o di flusso (con la sua cartella). Lo dice il
 * percorso del file (`comuni/raccolta/…`, `flussi/goelett/…`, vedi il README della eval).
 * Senza percorso (Executor precedente) decide il campo `flussi`.
 * @param {{percorso?: string, flussi?: string[], tag?: string[]}} scenario
 * @returns {{tipo: 'comune'|'flusso', nome: string}}
 */
export const gruppoDi = (scenario) => {
  const [radice, sotto] = String(scenario?.percorso ?? '').split('/')
  if (radice === 'comuni' && sotto && !sotto.endsWith('.json'))
    return { tipo: 'comune', nome: sotto }
  if (radice === 'flussi' && sotto && !sotto.endsWith('.json'))
    return { tipo: 'flusso', nome: sotto }
  return scenario?.flussi?.length
    ? { tipo: 'flusso', nome: scenario.flussi.join('+') }
    : { tipo: 'comune', nome: scenario?.tag?.[0] ?? 'altri' }
}

/**
 * Il catalogo diviso in comuni e di flusso, ognuno per cartella, nell'ordine del catalogo.
 * @param {Array<Object>} scenari
 * @returns {{comuni: Array<{nome: string, scenari: Array<Object>}>, flussi: Array<{nome: string, scenari: Array<Object>}>}}
 */
export const raggruppaCatalogo = (scenari = []) => {
  const comuni = new Map()
  const flussi = new Map()
  for (const scenario of scenari) {
    const { tipo, nome } = gruppoDi(scenario)
    const mappa = tipo === 'comune' ? comuni : flussi
    if (!mappa.has(nome)) mappa.set(nome, [])
    mappa.get(nome).push(scenario)
  }
  const elenco = (mappa) => [...mappa.entries()].map(([nome, lista]) => ({ nome, scenari: lista }))
  return { comuni: elenco(comuni), flussi: elenco(flussi) }
}

/** I preset del catalogo nel form di lancio. */
export const PRESET_CATALOGO = ['tutti', 'comuni', 'flussi', 'falliti']

/**
 * Il catalogo filtrato dal testo cercato e dal preset.
 * @param {Array<Object>} scenari
 * @param {{testo?: string, preset?: string, falliti?: string[]}} filtro - `falliti`: gli id
 *   sotto soglia nell'ultima run, per il preset «falliti»
 * @returns {Array<Object>}
 */
export const filtraCatalogo = (
  scenari = [],
  { testo = '', preset = 'tutti', falliti = [] } = {}
) => {
  const cercato = testo.trim().toLowerCase()
  return scenari.filter((s) => {
    if (preset === 'comuni' && gruppoDi(s).tipo !== 'comune') return false
    if (preset === 'flussi' && gruppoDi(s).tipo !== 'flusso') return false
    if (preset === 'falliti' && !falliti.includes(s.id)) return false
    if (!cercato) return true
    return [s.id, s.descrizione, ...(s.tag ?? [])].some((v) =>
      String(v ?? '')
        .toLowerCase()
        .includes(cercato)
    )
  })
}

/**
 * Il piano di una campagna su più environment: i comuni solo sul primo, quelli di flusso su
 * ciascuno; in ogni environment solo ciò che lì gira (`nonApplicabile` del suo catalogo).
 * È lo stesso piano di `yarn eval:agent --environment=1,5,7` (`pianoMultiEnvironment`).
 * @param {string[]} scelti - Gli id scelti nel form (vuoto: tutti)
 * @param {Array<{environmentId: number, catalogo?: {scenari: Array<Object>}}>} environments -
 *   Nell'ordine scelto, ognuno col catalogo letto per lui
 * @param {number} [ripetizioni] - Le ripetizioni del form, al posto di quelle degli scenari
 * @returns {Array<{environmentId: number, ids: string[], esecuzioni: number}>}
 */
export const pianoCampagna = (scelti = [], environments = [], ripetizioni) =>
  environments.map(({ environmentId, catalogo }, indice) => {
    const scenari = (catalogo?.scenari ?? []).filter(
      (s) =>
        !s.nonApplicabile &&
        (!scelti.length || scelti.includes(s.id)) &&
        (indice === 0 || gruppoDi(s).tipo === 'flusso')
    )
    const volte = Number(ripetizioni)
    return {
      environmentId: Number(environmentId),
      ids: scenari.map((s) => s.id),
      esecuzioni: scenari.reduce(
        (somma, s) => somma + (Number.isInteger(volte) && volte > 0 ? volte : (s.ripetizioni ?? 1)),
        0
      )
    }
  })

/**
 * Su quali environment della campagna gira uno scenario.
 * @param {string} id
 * @param {Array<{environmentId: number, ids: string[]}>} piano
 * @returns {number[]}
 */
export const environmentDelloScenario = (id, piano = []) =>
  piano.filter((voce) => voce.ids.includes(id)).map((voce) => voce.environmentId)

/**
 * Stima di costo e durata prima di lanciare, dalle run passate: costo medio per esecuzione
 * (`costoPerPasso` della testata) e secondi medi per esecuzione delle run concluse in pagina.
 * @param {number} esecuzioni
 * @param {{costoPerPasso?: Array<{valuta: string, totale: number}>}} misure
 * @param {Array<Object>} runs - Le run in pagina
 * @returns {{esecuzioni: number, costo: Array<{valuta: string, totale: number}>, minuti: number|null}}
 */
export const stimaCampagna = (esecuzioni, misure = {}, runs = []) => {
  const durate = runs
    .filter((r) => r.stato === 'completata' && r.conclusaIl && (r.passi?.fatti ?? 0) > 0)
    .map(
      (r) => (new Date(r.conclusaIl).getTime() - new Date(r.avviataIl).getTime()) / r.passi.fatti
    )
    .filter((ms) => Number.isFinite(ms) && ms > 0)
  const msPerEsecuzione = durate.length ? durate.reduce((a, b) => a + b, 0) / durate.length : null
  return {
    esecuzioni,
    costo: (misure.costoPerPasso ?? []).map((v) => ({
      ...v,
      totale: Math.round(v.totale * esecuzioni * 100) / 100
    })),
    minuti: msPerEsecuzione ? Math.max(1, Math.round((msPerEsecuzione * esecuzioni) / 60000)) : null
  }
}

/**
 * Il nome di una run quando chi lancia non lo scrive: giorno, ora ed environment.
 * @param {Array<number|string>} environmentIds
 * @param {Date} [adesso]
 * @returns {string}
 */
export const nomePredefinito = (environmentIds = [], adesso = new Date()) => {
  const due = (n) => String(n).padStart(2, '0')
  const data = `${due(adesso.getDate())}/${due(adesso.getMonth() + 1)} ${due(adesso.getHours())}:${due(adesso.getMinutes())}`
  return `eval ${data} · env ${environmentIds.join(', ')}`
}

/** Un id di campagna, come lo genera la CLI. */
export const nuovaCampagnaId = (adesso = new Date()) =>
  `${adesso.toISOString().replace(/[:.]/g, '-').slice(0, 19)}-${Math.random().toString(36).slice(2, 8)}`

/**
 * Le righe dell'elenco con le run della stessa campagna raccolte sotto una testata, nella
 * posizione della prima. La testata somma scenari, passi e costo delle sue run.
 * @param {Array<Object>} runs
 * @returns {Array<{tipo: 'campagna', campagna: Object, runs: Array<Object>, verdi: number, eseguiti: number, rossi: number, costo: Array<Object>} | {tipo: 'run', run: Object}>}
 */
export const righeConCampagne = (runs = []) => {
  const righe = []
  const viste = new Map()
  for (const run of runs) {
    const id = run.campagna?.id
    if (!id) {
      righe.push({ tipo: 'run', run })
      continue
    }
    if (!viste.has(id)) {
      const gruppo = { tipo: 'campagna', campagna: run.campagna, runs: [] }
      viste.set(id, gruppo)
      righe.push(gruppo)
    }
    viste.get(id).runs.push(run)
  }
  return righe.map((riga) => {
    if (riga.tipo !== 'campagna') return riga
    const somma = riga.runs.reduce(
      (acc, run) => {
        const { verdi, eseguiti } = riassuntoRun(run)
        acc.verdi += verdi
        acc.eseguiti += eseguiti
        acc.rossi += passiRun(run).rossi ?? 0
        return acc
      },
      { verdi: 0, eseguiti: 0, rossi: 0 }
    )
    return { ...riga, ...somma, costo: sommaCosti(riga.runs.flatMap((run) => costoRun(run))) }
  })
}

/**
 * I risultati di una run in tre blocchi da leggere in quest'ordine: sotto soglia (e in corso),
 * sopra soglia, saltati.
 * @param {Array<Object>} risultati
 * @returns {{rossi: Array<Object>, verdi: Array<Object>, saltati: Array<Object>}}
 */
export const divisiPerEsito = (risultati = []) => ({
  rossi: risultati.filter((r) => !r.saltato && r.passato !== true),
  verdi: risultati.filter((r) => !r.saltato && r.passato === true),
  saltati: risultati.filter((r) => r.saltato)
})

/**
 * Il corpo per rilanciare i soli scenari sotto soglia di una run, con le stesse opzioni.
 * @param {Object} run
 * @returns {Object|null} Il corpo di `create`, o null se non c'è niente da rifare
 */
export const corpoRipetiFalliti = (run) => {
  const base = corpoRipeti(run)
  const falliti = (run?.risultati ?? [])
    .filter((r) => !r.saltato && r.passato === false)
    .map((r) => r.id)
  if (!base || !falliti.length) return null
  return { ...base, scenari: falliti, nome: `${nomeRun(run)} · falliti`.slice(0, 80) }
}

/**
 * Divide il catalogo fra gli scenari che girano sull'environment scelto e quelli che
 * l'Executor salterebbe (`nonApplicabile`, messo da BusinessAI). Senza environment tutto
 * resta fra i selezionabili: la pagina chiede prima di sceglierne uno.
 * @param {Array<Object>} scenari - Il catalogo
 * @returns {{applicabili: Array<Object>, esclusi: Array<Object>}} Le due parti
 */
export const separaApplicabili = (scenari = []) => ({
  applicabili: scenari.filter((s) => !s.nonApplicabile),
  esclusi: scenari.filter((s) => s.nonApplicabile)
})

/**
 * Gli scenari scritti solo per questo flusso: `flussi` contiene il flusso e non è vuoto.
 * Uno scenario senza `flussi` gira ovunque e non è esclusivo.
 * @param {Array<{flussi?: string[]}>} scenari
 * @param {string} flusso
 */
export const soloDiFlusso = (scenari = [], flusso) =>
  flusso ? scenari.filter((s) => s.flussi?.length === 1 && s.flussi[0] === flusso) : scenari

/**
 * Perché uno scenario non gira sull'environment, in una riga.
 * @param {Object} motivo - `nonApplicabile` dello scenario
 * @param {Object} testi - Le traduzioni della pagina
 * @returns {string} La riga
 */
export const descriviNonApplicabile = (motivo, testi) => {
  if (motivo?.codice === 'FLUSSO')
    return `${testi.aeSoloPer} ${(motivo.flussi ?? []).map((f) => nomeFlusso(f)).join(', ')}`
  if (motivo?.codice === 'CAPABILITY')
    return `${testi.aeCapabilitySpenta}: ${(motivo.capability ?? []).join(', ')}`
  // I motivi della piattaforma (`nora/eval/applicability.ts`).
  if (motivo?.codice === 'RICHIEDE_VIAGGIO') return testi.aeRichiedeViaggio
  return testi.aeNonAgentico
}

/** Quante run per pagina nell'elenco. */
export const RIGHE_PER_PAGINA = 15

const MODI = {
  dynamic_demo: 'Demo Dinamico',
  midoffice_case: 'Pratica Midoffice',
  goelett_booking: 'Prenotazione Goelett',
  process_unit: 'Process-Unit'
}

/**
 * Il nome dell'environment, come lo mostra il form di lancio.
 * @param {number} id - L'environment della run
 * @param {Array<{id: number, societyName?: string, codSociety?: string}>} environments
 * @returns {string} Il nome, o l'id se l'elenco non c'è ancora
 */
export const nomeEnvironment = (id, environments = []) => {
  const trovato = environments.find((e) => String(e.id) === String(id))
  if (!trovato) return id == null ? '—' : String(id)
  if (trovato.societyName) return `${trovato.societyName} (${trovato.codSociety})`
  return trovato.codSociety || String(id)
}

/**
 * Il flusso della run, con il nome della modalità.
 * @param {string} [mode] - `contratto.mode`
 * @returns {string} Il nome, o il codice se non è fra quelli noti
 */
export const nomeFlusso = (mode) => (mode ? (MODI[mode] ?? mode) : '—')

/**
 * Giorno e ora di una run, in due righe.
 * @param {string} iso - Istante ISO
 * @returns {{giorno: string, ora: string}} Es. `{ giorno: '23/09/2026', ora: '21:05' }`
 */
export const giornoOra = (iso) => {
  const data = new Date(iso)
  if (Number.isNaN(data.getTime())) return { giorno: iso ?? '', ora: '' }
  const due = (n) => String(n).padStart(2, '0')
  return {
    giorno: `${due(data.getDate())}/${due(data.getMonth() + 1)}/${data.getFullYear()}`,
    ora: `${due(data.getHours())}:${due(data.getMinutes())}`
  }
}

/**
 * Il giorno locale di una run, `YYYY-MM-DD`: è il valore del filtro per data.
 * @param {string} iso - Istante ISO
 * @returns {string} Il giorno, o stringa vuota
 */
export const giornoRun = (iso) => {
  const data = new Date(iso)
  if (Number.isNaN(data.getTime())) return ''
  const due = (n) => String(n).padStart(2, '0')
  return `${data.getFullYear()}-${due(data.getMonth() + 1)}-${due(data.getDate())}`
}

/**
 * Le run che passano i filtri. Un filtro vuoto non taglia.
 * @param {Array<Object>} runs
 * @param {{testo?: string, environmentId?: string, flusso?: string, stato?: string, dal?: string, al?: string}} filtri
 * @returns {Array<Object>} Le run, nello stesso ordine
 */
export const filtraRun = (runs = [], filtri = {}) => {
  const testo = filtri.testo?.trim().toLowerCase()
  return runs.filter((run) => {
    if (filtri.environmentId && String(run.environmentId) !== String(filtri.environmentId))
      return false
    if (filtri.flusso && (run.contratto?.mode ?? '') !== filtri.flusso) return false
    if (filtri.stato && run.stato !== filtri.stato) return false
    const giorno = giornoRun(run.avviataIl)
    if (filtri.dal && giorno && giorno < filtri.dal) return false
    if (filtri.al && giorno && giorno > filtri.al) return false
    if (!testo) return true
    const dove = [nomeRun(run), run.id, run.modello, run.firmaHarness]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    return dove.includes(testo)
  })
}

/**
 * Una pagina dell'elenco, con il totale.
 * @param {Array<Object>} runs - Già filtrate
 * @param {number} pagina - Da 1
 * @param {number} [perPagina]
 * @returns {{pagina: number, pagine: number, totale: number, righe: Array<Object>}}
 */
export const paginaRun = (runs = [], pagina = 1, perPagina = RIGHE_PER_PAGINA) => {
  const pagine = Math.max(1, Math.ceil(runs.length / perPagina))
  const corrente = Math.min(Math.max(1, pagina), pagine)
  const da = (corrente - 1) * perPagina
  return { pagina: corrente, pagine, totale: runs.length, righe: runs.slice(da, da + perPagina) }
}

/**
 * La data di una run, breve e locale.
 * @param {string} iso - Istante ISO
 * @returns {string} Es. `23/09 21:05`
 */
export const quando = (iso) => {
  const data = new Date(iso)
  if (Number.isNaN(data.getTime())) return iso ?? ''
  const due = (n) => String(n).padStart(2, '0')
  return `${due(data.getDate())}/${due(data.getMonth() + 1)} ${due(data.getHours())}:${due(data.getMinutes())}`
}

/* ── Stato della pagina: filtri, avanzamento, risultati, stima ── */

/** I filtri dell'elenco run senza nessuna scelta. */
export const FILTRI_VUOTI = {
  testo: '',
  environmentId: '',
  flusso: '',
  stato: '',
  versioneHarness: '',
  dal: '',
  al: ''
}

/** I filtri che stanno nel pannello «Altri filtri» (gli altri sono sempre in vista). */
export const FILTRI_EXTRA = ['flusso', 'stato', 'dal', 'al']

/**
 * Quanti filtri sono accesi, tutti o solo quelli del pannello extra.
 * @param {Object} filtri - Lo stato dei filtri
 * @param {string[]} [campi] - I campi da contare (default: tutti)
 * @returns {number} Il numero di filtri con un valore
 */
export const contaFiltri = (filtri = {}, campi = Object.keys(FILTRI_VUOTI)) =>
  campi.filter((campo) => Boolean(filtri[campo])).length

/** Una run ancora viva: in coda o in corso. */
export const runAttiva = (run) => run?.stato === 'in_corso' || run?.stato === 'in_coda'

/**
 * L'avanzamento di una run in percentuale 0–100 sui passi previsti.
 * @param {Object} run - La run
 * @returns {number} Percentuale intera, 0 se i passi totali non si conoscono
 */
export const percentualeAvanzamento = (run) => {
  const passi = passiRun(run)
  const fatti = (passi.fatti ?? 0) + (passi.saltati ?? 0)
  if (!passi.totali) return 0
  return Math.min(100, Math.round((fatti / passi.totali) * 100))
}

/**
 * La variante del badge per lo stato di una run.
 * @param {string} stato - `in_corso`, `in_coda`, `completata`, `fermata`, `fallita`
 * @returns {string} Il modificatore di `nbadge`, vuoto per lo stato neutro
 */
export const varianteStato = (stato) =>
  ({
    in_corso: 'nbadge--info',
    in_coda: 'nbadge--info',
    completata: 'nbadge--ok',
    fermata: 'nbadge--warn',
    fallita: 'nbadge--err'
  })[stato] ?? ''

/**
 * Durata in secondi leggibile: `850 ms`, `12.4 s`, `3 min 05 s`.
 * @param {number} ms - Millisecondi
 * @returns {string} La durata, `—` se manca
 */
export const formattaDurata = (ms) => {
  if (typeof ms !== 'number' || Number.isNaN(ms)) return '—'
  if (ms < 1000) return `${Math.round(ms)} ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)} s`
  const minuti = Math.floor(ms / 60000)
  const secondi = Math.round((ms % 60000) / 1000)
  return `${minuti} min ${String(secondi).padStart(2, '0')} s`
}

/**
 * Se un'esecuzione va letta: errore tecnico o controlli caduti (conversazione), mossa
 * sbagliata o non valida (punto di decisione).
 * @param {Object} esecuzione - L'esecuzione di uno scenario
 * @param {'conversazione'|'decisione'} tipo - Il tipo dello scenario
 * @returns {boolean} true se è verde
 */
export const esecuzioneVerde = (esecuzione, tipo) =>
  tipo === 'decisione'
    ? Boolean(esecuzione?.attesaEScrittaBene)
    : !esecuzione?.errore && !(esecuzione?.verdetti ?? []).some((v) => !v.passato)

/**
 * Gli scenari di una run che passano la ricerca e, se richiesto, solo quelli da rivedere
 * (sotto soglia o ancora in corso). La ricerca guarda id, descrizione, situazione e tag.
 * @param {Array<Object>} risultati - I risultati della run
 * @param {{testo?: string, soloDaRivedere?: boolean}} filtro
 * @returns {Array<Object>} I risultati, nello stesso ordine
 */
export const filtraRisultati = (risultati = [], { testo = '', soloDaRivedere = false } = {}) => {
  const cerca = testo.trim().toLowerCase()
  return risultati.filter((r) => {
    if (soloDaRivedere && (r.saltato || r.passato === true)) return false
    if (!cerca) return true
    return [r.id, r.descrizione, r.situazione, ...(r.tag ?? [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(cerca)
  })
}

/**
 * Aggiunge alle voci dell'analisi le quote già calcolate, pronte da mostrare.
 * @param {Array<Object>} voci - `difficolta.voci` della risposta del server
 * @returns {Array<Object>} Le voci con `quota`, `quotaRunSottoSoglia`, `quotaFallite`
 */
export const voceDifficolta = (voci = []) =>
  voci.map((voce) => ({
    ...voce,
    quota: voce.prove ? voce.successi / voce.prove : 0,
    quotaRunSottoSoglia: voce.run ? voce.runSottoSoglia / voce.run : 0,
    quotaFallite: voce.prove ? (voce.prove - voce.successi) / voce.prove : 0,
    quotaInterruzioni: voce.prove ? (voce.interruzioni ?? 0) / voce.prove : 0,
    quotaCaduto: voce.prove && voce.volteCaduto ? voce.volteCaduto / voce.prove : 0
  }))

/**
 * La riga di stima sotto «Avvia»: esecuzioni, ripartizione per environment, costo e tempo.
 * @param {{esecuzioni: number, costo: Array<Object>, minuti: number}} stima - `stimaCampagna`
 * @param {Array<{environmentId: number, esecuzioni: number}>} piano - `pianoCampagna`
 * @param {{esecuzioni: string, minuti: string}} testi - Le parole da usare
 * @returns {string} La riga, con le voci separate da « · »
 */
export const descriviStima = (stima, piano = [], testi) =>
  [
    `${stima.esecuzioni} ${testi.esecuzioni}`,
    ...(piano.length > 1
      ? piano.map((voce) => `env ${voce.environmentId}: ${voce.esecuzioni}`)
      : []),
    stima.costo?.length ? `~${descriviCosto(stima.costo)}` : null,
    stima.minuti ? `~${stima.minuti} ${testi.minuti}` : null
  ]
    .filter(Boolean)
    .join(' · ')

/**
 * Perché «Avvia» è spento, in una frase; stringa vuota se si può lanciare.
 * @param {{ambienti: number, pronti: boolean, esecuzioni: number, invio: boolean}} stato
 * @param {Object} testi - Le traduzioni
 * @returns {string} Il motivo
 */
export const motivoNonLanciabile = ({ ambienti, pronti, esecuzioni, invio }, testi) => {
  if (invio) return ''
  if (!ambienti) return testi.aeNessunoScelto
  if (!pronti) return testi.caricamento
  if (!esecuzioni) return testi.aeNienteDaLanciare
  return ''
}

/**
 * Gli environment salvati nel browser (ordine di scelta), anche con la chiave di prima.
 * @param {Pick<Storage, 'getItem'>} [storage] - Dove leggere (di default `localStorage`)
 * @returns {string[]} Gli id come stringhe
 */
export const leggiEnvironmentSalvati = (storage = globalThis.localStorage) => {
  try {
    const salvati = JSON.parse(storage.getItem('aeEnvironmentIds') || '[]')
    if (Array.isArray(salvati) && salvati.length) return salvati.map(String)
    const vecchio = storage.getItem('aeEnvironmentId')
    return vecchio ? [vecchio] : []
  } catch {
    return []
  }
}

/**
 * Salva gli environment scelti, senza mai far fallire la pagina se lo storage non c'è.
 * @param {string[]} ids - Gli id nell'ordine di scelta
 * @param {Pick<Storage, 'setItem'>} [storage]
 */
export const salvaEnvironment = (ids, storage = globalThis.localStorage) => {
  try {
    storage.setItem('aeEnvironmentIds', JSON.stringify(ids))
  } catch {
    /* localStorage non disponibile: la scelta vale solo per questa sessione */
  }
}

/**
 * Aggiunge o toglie un id dall'elenco, tenendo l'ordine di scelta (il primo ha i comuni).
 * @param {string[]} ids - La scelta corrente
 * @param {string|number} id - L'environment cliccato
 * @returns {string[]} La nuova scelta
 */
export const commutaId = (ids, id) =>
  ids.includes(String(id)) ? ids.filter((x) => x !== String(id)) : [...ids, String(id)]

/**
 * Quanti scenari di un gruppo sono selezionati.
 * @param {string[]} ids - Gli scenari del gruppo
 * @param {string[]} selezionati - La selezione del form
 * @returns {{scelti: number, totali: number}} I conteggi
 */
export const conteggioGruppo = (ids = [], selezionati = []) => ({
  scelti: ids.filter((id) => selezionati.includes(id)).length,
  totali: ids.length
})

/**
 * Un gruppo del catalogo parte aperto solo se ha selezioni o se c'è una ricerca in corso
 * (e quindi i suoi scenari sono quelli trovati).
 * @param {{scelti: number}} conteggio - `conteggioGruppo`
 * @param {string} [ricerca] - Il testo cercato
 * @returns {boolean} true se va aperto
 */
export const gruppoApertoDiDefault = (conteggio, ricerca = '') =>
  conteggio.scelti > 0 || ricerca.trim().length > 0
